// composables/themes/layout/useHeaderNavigation.ts
import { MENU_CODE } from '@/constants/menu'

export const useHeaderNavigation = () => {
  const router = useRouter()
  const route = useRoute()
  const { localePath, basePath } = useLanguage()
  const { menus, getPath, redirectPath, slugPageInfo } = useNavigationMenu()
  const { analyticEvent, fbPixelEvent } = useAnalytic()

  const activeDropdown = ref<number | null>(null)

  /**
   * Handle mouse enter on menu item
   * @param index Menu item index
   */
  const handleMouseEnter = (index: number) => {
    activeDropdown.value = index
  }

  /**
   * Handle mouse leave on menu item
   */
  const handleMouseLeave = () => {
    activeDropdown.value = null
  }

  /**
   * Handle menu item click
   * @param item Menu item
   */
  const handleMenuClick = (item: Menu) => {
    // Prevent redirect only on gallery review
    if (item.code === MENU_CODE.GALLERY_REVIEW) {
      return
    }

    if (item.slug) {
      router.push(localePath(`/${item.slug}`))
      eventTopMenu(item.title, item.code)
    }
  }

  /**
   * Track menu click event
   * @param title Menu title
   * @param code Menu code
   */
  const eventTopMenu = (title: string, code: string) => {
    if (code !== MENU_CODE.GALLERY_REVIEW) {
      analyticEvent({ ga4ActionName: 'click_top_menu', ga4Label: title })
      fbPixelEvent({ fbCategory: 'trackCustom', fbEvent: 'Top Menu', fbValue: { label: title } })
    }
  }

  /**
   * Check if menu item is active
   * @param menuItem Menu item
   * @returns Whether menu item is active
   */
  const isActive = (menuItem: Menu) => {
    if (menuItem.code === MENU_CODE.EXTERNAL_LINK) {
      return false
    }
    if (basePath(route.path) === '/' && menuItem.code === MENU_CODE.HOME) {
      return true
    } else if (basePath(route.path) !== '/' && !menuItem.slug) {
      return false
    }

    if (menuItem.code === MENU_CODE.GALLERY_REVIEW) {
      const filterGallery = menuItem.subMenus.map(item => item.slug)
      const pageSlug = slugPageInfo.value?.slug || ''
      const checkMenu = filterGallery.includes(pageSlug)
      return checkMenu
    }

    return slugPageInfo.value?.slug === menuItem.slug
  }

  /**
   * Check if menu item is nearly at the last position
   * to adjust dropdown position
   * @param position Menu item position
   * @returns Whether menu item is nearly at the last position
   */
  const isNearlyLastPosition = (position: number) =>
    menus.value.length - position <= 3

  return {
    menus,
    activeDropdown,
    handleMouseEnter,
    handleMouseLeave,
    handleMenuClick,
    eventTopMenu,
    isActive,
    isNearlyLastPosition,
    redirectPath,
    getPath,
  }
}
